import { useEffect, useState } from 'react';
import { Button } from '../../../../components/Button';
import { Modal } from '../../../../components/Modal';
import { TextField } from '../../../../components/inputs/TextField';
import { ApiError } from '../../../../lib/apiError';
import { uid } from '../../../../lib/uid';
import { focusValidationError } from '../../../../lib/validationFocus';
import { usersApi } from '../usersApi';
import type { UserDetail } from '../userTypes';

/**
 * The Basic Info card's editor — the alias, and nothing else.
 *
 * One card, one modal. It carries only the fields of the section it was opened from, so the
 * user is never shown a form covering things they did not come to change.
 *
 * Roles are edited from their own card; the email is the sign-in identity and is shown here
 * read-only because leaving it out entirely would make the modal look like it had lost a
 * field the card displays.
 */
export function EditUserInfoModal({
  user,
  onClose,
  onSaved,
}: {
  user: UserDetail | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [alias, setAlias] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  useEffect(() => {
    if (!user) return;

    setAlias(user.alias ?? '');
    setConfirmed(false);
    setError(null);
  }, [user]);

  if (!user) return null;

  const canSave = confirmed && !saving;

  async function save() {
    if (!user || !canSave) return;

    setSaving(true);
    setError(null);

    try {
      // The roles this account already holds are sent back unchanged: the update contract
      // replaces the whole set, so omitting them here would strip them.
      await usersApi.update(user.id, {
        alias: alias.trim() || null,
        roleId: user.roleId ?? '',
      });

      onSaved();
    } catch (caught) {
      const failure = caught instanceof ApiError ? caught : new ApiError('Could not update the account.', 0);

      setError(failure);

      // Say which field, show it, and — on a stepped form — go to its step.
      focusValidationError(failure, { idPrefix: 'edit-user-info' });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title="Edit basic info"
      size="md"
      id="edit-user-info-modal"
      /*
        Autogenerate on an edit form — see CLAUDE.md. The alias is the only field here, and a
        suffixed one so repeated clicks do not collide on a value the server treats as unique.
      */
      autogenerate={{
        onGenerate: () => setAlias(`probe.${uid().slice(0, 6).toLowerCase()}`),
        disabled: saving,
      }}
      confirm={{
        id: 'edit-user-info-confirm',
        checked: confirmed,
        onChange: setConfirmed,
        disabled: saving,
      }}
      footer={
        <>
          <Button id="edit-user-info-cancel" variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            id="edit-user-info-submit"
            variant="primary"
            onClick={() => void save()}
            disabled={!canSave}
            loading={saving}
            loadingLabel="Submitting"
          >
            Submit
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error ? (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-xs text-red-700">{error.message}</p>
          </div>
        ) : null}

        <TextField
          id="edit-user-info-alias"
          label="Alias"
          value={alias}
          onChange={(e) => setAlias(e.target.value)}
          error={error?.fieldError('alias')}
          description="Short handle. The account falls back to its email wherever this is blank."
          disabled={saving}
        />

        <TextField
          id="edit-user-info-email"
          label="Email"
          value={user.email}
          disabled
          description="The sign-in address cannot be changed after the account is created."
        />

      </div>
    </Modal>
  );
}
